import React, {
  useState, ReactNode, useRef, useLayoutEffect, forwardRef, PropsWithChildren,
} from 'react';
import styled, { FlattenInterpolation, ThemeProps } from 'styled-components';
import { Input, InputCss, InputFocus } from './Input';
import { ThemeType, useFormConfigurationContext } from '../../../..';
import { FlexGrowCss } from '../../Flex';

export const TextFieldInput = styled.input<{
  styles?: string,
  hasError: boolean,
  paddingLeft?: number,
  paddingRight?: number,
  small?: boolean,
  xs?: boolean,
  isSuccess?: boolean,
  readOnly?: boolean
}>`
  ${InputCss};
  ${({
    theme, disabled, hasError, xs, small, isSuccess,
  }) => `
    background-color: ${xs ? theme.colours.neutral[9] : theme.colours.neutral[10]};
    font-weight: ${theme.fontWeight.normal};
    padding: ${theme.padding[2]} ${(xs || small) ? `${theme.padding[2]} !important` : theme.padding[3]};
    border-color: ${hasError ? theme.textDanger : theme.colours.neutral[6]};
    ${!disabled && `
      &:hover {
        background-color: ${theme.colours.neutral[10]};
        border-color: ${hasError ? theme.textDanger : theme.colours.primary[5]};
      }
    `};
    &:disabled {
      background-color: ${theme.colours.neutral[8]};
      border-color: ${theme.colours.neutral[8]};
    }
    &::placeholder {
      color:  ${theme.colours.neutral[5]};
      font-size: ${theme.fontSize[2]};
      font-weight: ${theme.fontWeight.normal};
    }
    ${isSuccess && `border-color: ${theme.colours.success[1]} !important`};
  `};
  ${({ theme, paddingLeft }) => `padding-left: ${paddingLeft ? `${paddingLeft}px` : theme.fontSize[2]}`};
  ${({ theme, paddingRight }) => `padding-right: ${paddingRight ? `${paddingRight}px` : theme.fontSize[2]}`};
  ${InputFocus}
`;

const ComponentPrefixWrapper = styled.div` ${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[2]};
  position: absolute;
  box-sizing: border-box;
  top: 50%;
  transform: translateY(-50%);
  padding-left: ${theme.fontSize[2]};
  padding-right: ${theme.padding[2]};
  font-weight: ${theme.fontWeight.normal};
`}`;

const ComponentSuffixWrapper = styled.div<{ xs?: boolean, css?: FlattenInterpolation<ThemeProps<ThemeType>> | string }>`
  ${({ theme, xs }) => `
    position: absolute;
    box-sizing: border-box;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    padding: ${theme.padding[2]};
    padding-right: ${theme.padding[3]};
    font-weight: ${theme.fontWeight.normal};
    font-size: ${xs ? theme.fontSize[1] : theme.fontSize[2]};
    display: flex;
    height: 100%;
    align-items: center;
  `}; 

  ${({ css }) => css};
`;

const TextInputWrapper = styled.div<{ styles?: string }>` ${({ styles }) => `
  position: relative;
  ${FlexGrowCss};
  ${styles || ''}
`}`;

export type TextFieldProps = {
  id: string,
  label?: ReactNode,
  placeholder?: string,
  value: string,
  onChange: (v: string, e?: React.ChangeEvent<HTMLInputElement>) => void,
  prefix?: ReactNode,
  suffix?: ReactNode,
  labelStyles?: string,
  styles?: string,
  containerStyles?: string,
  inputWrapperStyle?: string,
  type?: string,
  visible?: boolean,
  disabled?: boolean,
  touchOverride?: boolean,
  onFocus?: () => void,
  onClick?: () => void,
  onBlur?: (targetValue: string) => void,
  onClickSuffix?: () => void,
  required?: boolean,
  small?: boolean,
  xs?: boolean,
  inline?: boolean,
  suffixCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  isTooltipError?: boolean,
  isSuccess?: boolean,
  hideErrorMessage?: boolean,
  readOnly?: boolean,
  tooltip?: ReactNode,
  changeOnBlur?: boolean,
  mutateValue?: (v: string) => string,
  errors?: string[],
  warnings?: string[],
}

export const TextInput = forwardRef<HTMLInputElement, PropsWithChildren<TextFieldProps>>(({
  id,
  label = '',
  placeholder = '',
  value = '',
  onChange,
  prefix,
  suffix,
  labelStyles = '',
  styles = '',
  containerStyles = '',
  inputWrapperStyle = '',
  type = 'text',
  visible = true,
  disabled = false,
  touchOverride = false,
  onFocus = () => { },
  onBlur = () => { },
  onClick = () => { },
  onClickSuffix = () => { },
  required = false,
  small = false,
  xs = false,
  inline = false,
  suffixCss = '',
  isTooltipError = false,
  isSuccess = false,
  hideErrorMessage = false,
  readOnly = false,
  tooltip,
  changeOnBlur = false,
  mutateValue = (v) => v,
  children,
  errors = [],
  warnings = [],
}, ref) => {
  const [touched, setTouched] = useState(false);
  const hasError = (touched || touchOverride) && !!errors?.length;

  const { isDisabled } = useFormConfigurationContext(disabled);

  const onInputChange = (targetValue: string, element: React.ChangeEvent<HTMLInputElement>) => {
    if (!changeOnBlur) {
      onChange(targetValue, element);
    }
  };

  const onTouched = (targetValue: string, element: React.ChangeEvent<HTMLInputElement>) => {
    setTouched(true);
    if (changeOnBlur) onChange(targetValue, element);
    onBlur(targetValue);
  };

  const componentPrefixWrapperRef = useRef<null | HTMLDivElement>(null);
  const componentSuffixWrapperRef = useRef<null | HTMLDivElement>(null);

  const [paddingLeft, setPaddingLeft] = useState<number>(0);
  const [paddingRight, setPaddingRight] = useState<number>(0);

  useLayoutEffect(() => {
    setPaddingLeft(componentPrefixWrapperRef?.current?.clientWidth ?? 0);
  }, [componentPrefixWrapperRef.current?.clientWidth, visible]);

  useLayoutEffect(() => {
    setPaddingRight(componentSuffixWrapperRef?.current?.clientWidth ?? 0);
  }, [componentSuffixWrapperRef.current?.clientWidth, visible]);

  return (
    <>
      {visible && (
        <Input
          id={id}
          label={label}
          hasError={hasError}
          errors={errors}
          warnings={warnings}
          labelStyles={labelStyles}
          containerStyles={containerStyles}
          required={required}
          inline={inline}
          disabled={isDisabled}
          isTooltipError={isTooltipError}
          hideErrorMessage={hideErrorMessage}
          tooltip={tooltip}
        >
          <TextInputWrapper styles={inputWrapperStyle}>
            {prefix && (
              <ComponentPrefixWrapper ref={componentPrefixWrapperRef}>
                {prefix}
              </ComponentPrefixWrapper>
            )}

            <TextFieldInput
              id={`${id}-input`}
              value={mutateValue(value)}
              onChange={(e) => onInputChange(mutateValue(e.target.value), e)}
              paddingLeft={paddingLeft}
              paddingRight={paddingRight}
              styles={styles}
              placeholder={placeholder}
              hasError={hasError}
              type={type}
              disabled={isDisabled}
              onFocus={onFocus}
              onBlur={(e) => onTouched(e.target.value, e)}
              onClick={onClick}
              small={small}
              xs={xs}
              ref={ref}
              autoComplete="off"
              isSuccess={isSuccess}
              readOnly={readOnly}
            />

            {suffix && (
              <ComponentSuffixWrapper onClick={onClickSuffix} ref={componentSuffixWrapperRef} xs={xs} css={suffixCss}>
                {suffix}
              </ComponentSuffixWrapper>
            )}
          </TextInputWrapper>
          {children}
        </Input>
      )}
    </>
  );
});
